import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { icons } from '../../../assets/icons';
import './createfacture.scss';
import { getProductsByAdmin, getCategoriesForProducts, clientsOrderListByAdmin, createFactureProforma } from '../../../api/redux/slices/Admin';
import { roundToThreeDecimals, typePrice } from '../../../helpers/modules';
import { calculateTotalTva } from '../../../utils/groupTvaValues';
import { showToast } from '../../../components/modal';
import { LoaderComponent } from '../../../helpers/components/loader';
import { TunisiaCities } from '../../../data';

const CreateFactureProforma = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = localStorage.getItem('accesstoken');

    const { productsArr, listCategoriesProducts, clientsListOrdering, loading } = useSelector((state) => state.admin);

    // State
    const [clientType, setClientType] = useState('existing');
    const [selectedClient, setSelectedClient] = useState('');
    const [selectedShop, setSelectedShop] = useState('');
    const [searchProduct, setSearchProduct] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [cart, setCart] = useState([]);
    const [clientSearch, setClientSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [allProducts, setAllProducts] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [countries, setCountries] = useState([]);
    
    const [newClientInfo, setNewClientInfo] = useState({
        full_name: '',
        company_name: '',
        phone: '',
        email: '',
        tax_registration_number: '',
        customs_code: '',
        categories: 'detaillant',
    });
    
    const [newShopInfo, setNewShopInfo] = useState({
        name: '',
        address_number: '',
        street: '',
        ville: '',
        postal_code: '',
        region: '',
        country: 'TUN', // Set default country to TUNISIA
    });

    // Fetch countries (alpha-3 codes, e.g. "TUN" for Tunisia)
    // IMPORTANT: we use ISO 3166-1 alpha-3 codes here so the value matches
    // the 'TUN' default/comparisons used throughout this component.
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch('https://cdn.jsdelivr.net/npm/world-countries@4/dist/countries.json');

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                const sortedCountries = data
                    .map((country) => ({
                        name: country?.name?.common,
                        code: country?.cca3, // alpha-3 code, e.g. "TUN" for Tunisia
                        flag: country?.flag,
                    }))
                    .filter((country) => country.name && country.code)
                    .sort((a, b) => a.name.localeCompare(b.name));

                setCountries(sortedCountries);
            } catch (err) {
                console.error('Error fetching countries:', err);
                showToast('Could not load countries list', 'error');

                // Fallback so Tunisia is always selectable even if the CDN fails
                setCountries([
                    { name: 'Tunisia', code: 'TUN', flag: '🇹🇳' },
                ]);
            }
        };

        fetchCountries();
    }, []);

    const selectedClientData = useMemo(() => {
        return clientsListOrdering?.find(c => c._id === selectedClient);
    }, [clientsListOrdering, selectedClient]);

    const shops = useMemo(() => {
        return selectedClientData?.shop || [];
    }, [selectedClientData]);

    const currentShop = useMemo(() => {
        if (clientType === 'existing') {
            return shops?.find(s => s._id === selectedShop);
        } else {
            return null;
        }
    }, [clientType, shops, selectedShop]);

    // Check if shop is in Tunisia (country code 'TUN')
    const isTunisia = useMemo(() => {
        if (clientType === 'existing') {
            // For existing client, check the selected shop's country
            if (!currentShop) return true; // Default to true if no shop selected
            return currentShop.country === 'TUN' || !currentShop.country;
        } else {
            // For new client, check the country from the form
            return newShopInfo.country === 'TUN';
        }
    }, [clientType, currentShop, newShopInfo.country]);
    
    const clientCategory = clientType === 'existing' 
        ? selectedClientData?.categories || 'detaillant'
        : newClientInfo.categories;

    // Fetch products - always show regardless of client selection
    useEffect(() => {
        dispatch(getProductsByAdmin({ 
            token, 
            name: undefined, 
            category_name: undefined,
            page: 1 
        }));
    }, [dispatch, token]);

    useEffect(() => {
        if (productsArr) {
            setAllProducts(productsArr);
        }
    }, [productsArr]);

    useEffect(() => {
        dispatch(getCategoriesForProducts({ token }));
    }, [dispatch, token]);

    useEffect(() => {
        if (clientType === 'existing' && clientSearch.length >= 2) {
            dispatch(clientsOrderListByAdmin({ token, value: clientSearch }));
            setShowDropdown(true);
        } else {
            setShowDropdown(false);
        }
    }, [dispatch, token, clientSearch, clientType]);

    const filteredProducts = useMemo(() => {
        if (!allProducts) return [];
        
        let result = [...allProducts];
        
        if (searchProduct.trim()) {
            const searchLower = searchProduct.toLowerCase().trim();
            result = result.filter(product => {
                const designation = (product?.designation || '').toLowerCase();
                const reference = (product?.reference || '').toLowerCase();
                return designation.includes(searchLower) || reference.includes(searchLower);
            });
        }
        
        if (selectedCategory) {
            result = result.filter(product => {
                const catName = product?.category?.designation 
                    || product?.category_id?.designation 
                    || product?.category_name 
                    || '';
                return catName === selectedCategory;
            });
        }
        
        return result;
    }, [allProducts, searchProduct, selectedCategory]);

    const handleAddToCart = (product) => {
        const existingItem = cart.find(item => item.product._id === product._id);
        if (existingItem) {
            setCart(cart.map(item =>
                item.product._id === product._id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
            ));
        } else {
            setCart([...cart, {
                product,
                quantity: 1,
                remise: 0,
            }]);
        }
        showToast('Produit ajouté', 'success', 'top-center', 1000);
    };

    const handleRemoveFromCart = (productId) => {
        setCart(cart.filter(item => item.product._id !== productId));
    };

    const handleQuantityChange = (productId, newQuantity) => {
        if (newQuantity < 1) return;
        setCart(cart.map(item =>
            item.product._id === productId
                ? { ...item, quantity: parseInt(newQuantity) || 0 }
                : item
        ));
    };

    const handleRemiseChange = (productId, newRemise) => {
        setCart(cart.map(item =>
            item.product._id === productId
                ? { ...item, remise: parseFloat(newRemise) || 0 }
                : item
        ));
    };

    const totalHT = () => {
        let total = 0;
        cart?.forEach((item) => {
            const price = item.product?.[typePrice(clientCategory)] || 0;
            total += item.quantity * price * (1 - (item.remise || 0) / 100);
        });
        return roundToThreeDecimals(total);
    };

    const totalTCC = () => {
        const list = cart.map((item) => ({
            tva: isTunisia ? (item?.product?.Tva || 0) : 0,
            prise_product_HT: item?.product?.[typePrice(clientCategory)] || 0,
            quantity: item?.quantity,
            remise: item?.remise || 0,
        }));

        const totalTvaSum = calculateTotalTva({ list }).reduce(
            (acc, { totalAmount }) => acc + totalAmount,
            0,
        );

        const totalHTAmount = totalHT();
        const timbre = isTunisia ? 1 : 0;

        return roundToThreeDecimals(Number(totalTvaSum) + Number(totalHTAmount) + timbre);
    };

    const handleCalculatePrice = (item) => {
        const price = item.product?.[typePrice(clientCategory)] || 0;
        const priceAfterRemise = price * (1 - (item.remise || 0) / 100);
        return roundToThreeDecimals(
            priceAfterRemise * (isTunisia ? (1 + (item.product?.Tva || 0) / 100) : 1) * item.quantity,
        );
    };

    const getCategoryName = (product) => {
        if (product?.category?.designation) return product.category.designation;
        if (product?.category_id?.designation) return product.category_id.designation;
        if (product?.category_name) return product.category_name;
        return '-';
    };

    const handleNewClientInputChange = (field, value) => {
        setNewClientInfo(prev => ({ ...prev, [field]: value }));
    };

    const handleNewShopInputChange = (field, value) => {
        setNewShopInfo(prev => ({ ...prev, [field]: value }));
    };

    const handleCountryChange = (countryCode) => {
        setNewShopInfo(prev => ({ 
            ...prev, 
            country: countryCode,
            region: '' // Reset region when country changes
        }));
    };

    const handleSubmitFacture = async () => {
        if (clientType === 'existing') {
            if (!selectedClient || !selectedShop || cart.length === 0) {
                showToast('Veuillez remplir tous les champs', 'warn');
                return;
            }
        } else {
            if (!newClientInfo.full_name || !newClientInfo.phone || !newShopInfo.name || !newShopInfo.ville || cart.length === 0) {
                showToast('Veuillez remplir tous les champs client et boutique', 'warn');
                return;
            }
            if (!newShopInfo.country) {
                showToast('Veuillez sélectionner un pays', 'warn');
                return;
            }
            // Region validation only for Tunisia
            if (newShopInfo.country === 'TUN' && !newShopInfo.region) {
                showToast('Le gouvernorat est obligatoire pour la Tunisie', 'warn');
                return;
            }
        }

        const list = cart.map(item => ({
            product_id: item.product._id,
            quantity: item.quantity,
            remise: item.remise || 0,
            prise_product_HT: item.product?.[typePrice(clientCategory)] || item.product?.Retail_Price || 0,
        }));

        let data = {};

        if (clientType === 'existing') {
            data = {
                client_type: 'existing',
                client_id: selectedClient,
                shop_id: selectedShop,
                list: list,
            };
        } else {
            data = {
                client_type: 'new',
                client_info: {
                    full_name: newClientInfo.full_name,
                    company_name: newClientInfo.company_name,
                    phone: newClientInfo.phone,
                    email: newClientInfo.email,
                    tax_registration_number: newClientInfo.tax_registration_number,
                    customs_code: newClientInfo.customs_code,
                    categories: newClientInfo.categories,
                },
                shop_info: {
                    name: newShopInfo.name,
                    address_number: newShopInfo.address_number,
                    street: newShopInfo.street,
                    ville: newShopInfo.ville,
                    postal_code: newShopInfo.postal_code,
                    region: newShopInfo.country === 'TUN' ? newShopInfo.region : '',
                    country: newShopInfo.country,
                },
                list: list,
            };
        }

        setIsSubmitting(true);
        try {
            await dispatch(createFactureProforma({ token, data })).unwrap();
            showToast('Facture proforma créée avec succès', 'success');
            navigate('/admin/bills/1');
        } catch (error) {
            const errorMessage = error?.message || '';
            const errorData = error?.data || [];
            
            if (errorData.length > 0) {
                const productNames = errorData.map(item => {
                    const cartItem = cart.find(c => c.product._id === item.product_id);
                    return cartItem ? cartItem.product?.designation : item.designation || 'Produit inconnu';
                }).join(', ');
                
                showToast(`${errorMessage} : ${productNames}`, 'error');
            } else {
                showToast(errorMessage || 'Erreur lors de la création de la facture', 'error');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValidForSubmission = () => {
        if (clientType === 'existing') {
            return selectedClient && selectedShop && cart.length > 0;
        } else {
            const baseValid = newClientInfo.full_name && newClientInfo.phone && newShopInfo.name && newShopInfo.ville && cart.length > 0 && newShopInfo.country;
            if (newShopInfo.country === 'TUN') {
                return baseValid && newShopInfo.region;
            }
            return baseValid;
        }
    };

    return (
        <div id="createFactureProforma">
            <header className="cfp-header">
                <div className="cfp-header__brand">
                    <span className="cfp-header__icon">📄</span>
                    <h1 className="cfp-header__title">Créer une Facture Proforma</h1>
                </div>
                <button className="cfp-back-btn" onClick={() => navigate('/admin/bills/1')}>
                    <icons.IoIosArrowBack />
                    <span>Retour</span>
                </button>
            </header>

            <div className="cfp-content">
                {/* Client Type Selection */}
                <div className="cfp-client-type">
                    <div className="cfp-select-group">
                        <label>Type de client</label>
                        <div className="cfp-radio-group">
                            <label className="cfp-radio-label">
                                <input
                                    type="radio"
                                    value="existing"
                                    checked={clientType === 'existing'}
                                    onChange={() => {
                                        setClientType('existing');
                                        setSelectedClient('');
                                        setSelectedShop('');
                                        setClientSearch('');
                                    }}
                                />
                                <span>Client existant</span>
                            </label>
                            <label className="cfp-radio-label">
                                <input
                                    type="radio"
                                    value="new"
                                    checked={clientType === 'new'}
                                    onChange={() => {
                                        setClientType('new');
                                        setSelectedClient('');
                                        setSelectedShop('');
                                        setClientSearch('');
                                        // Reset new shop info with TUN as default
                                        setNewShopInfo(prev => ({ ...prev, country: 'TUN', region: '' }));
                                    }}
                                />
                                <span>Nouveau client (non inscrit)</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Client & Shop Selection - Existing Client */}
                {clientType === 'existing' && (
                    <div className="cfp-selection">
                        <div className="cfp-select-group">
                            <label>Client</label>
                            <div className="cfp-search-select">
                                <input
                                    type="text"
                                    placeholder="Rechercher un client..."
                                    value={clientSearch}
                                    onChange={(e) => setClientSearch(e.target.value)}
                                    onClick={() => {
                                        if (clientsListOrdering?.length > 0 && !selectedClient) {
                                            setShowDropdown(true);
                                        }
                                    }}
                                    readOnly={!!selectedClient}
                                    className={selectedClient ? 'selected' : ''}
                                />
                                {selectedClient && selectedClientData && (
                                    <div className="cfp-selected-client">
                                        <div className="cfp-selected-client-info">
                                            <strong>{selectedClientData.company_name || selectedClientData.name}</strong>
                                            <span>{selectedClientData.email}</span>
                                        </div>
                                        <button
                                            className="cfp-change-client-btn"
                                            onClick={() => {
                                                setSelectedClient('');
                                                setSelectedShop('');
                                                setClientSearch('');
                                                setCart([]);
                                            }}
                                            title="Changer de client"
                                        >
                                            <icons.FiX />
                                        </button>
                                    </div>
                                )}
                                {!selectedClient && showDropdown && clientsListOrdering?.length > 0 && clientSearch.length >= 2 && (
                                    <div className="cfp-dropdown">
                                        {clientsListOrdering.map(client => (
                                            <div
                                                key={client._id}
                                                className="cfp-dropdown-item"
                                                onClick={() => {
                                                    setSelectedClient(client._id);
                                                    setSelectedShop('');
                                                    setClientSearch('');
                                                    setShowDropdown(false);
                                                }}
                                            >
                                                <span>{client.company_name || client.name}</span>
                                                <span className="client-email">{client.email}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {selectedClient && (
                            <div className="cfp-select-group">
                                <label>Boutique</label>
                                <select
                                    value={selectedShop}
                                    onChange={(e) => setSelectedShop(e.target.value)}
                                    className="cfp-select"
                                >
                                    <option value="">Sélectionner une boutique</option>
                                    {shops?.map((shop) => (
                                        <option key={shop._id} value={shop._id}>
                                            {shop.name} - {shop.ville}
                                            {shop.country && shop.country !== 'TUN' ? ` (${shop.country})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                {/* New Client Form */}
                {clientType === 'new' && (
                    <div className="cfp-new-client-section">
                        <div className="cfp-form-section">
                            <h3>Informations Client</h3>
                            <div className="cfp-form-grid">
                                <div className="cfp-form-group">
                                    <label>Nom complet *</label>
                                    <input
                                        type="text"
                                        value={newClientInfo.full_name}
                                        onChange={(e) => handleNewClientInputChange('full_name', e.target.value)}
                                        placeholder="Nom et prénom"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Nom de l'entreprise</label>
                                    <input
                                        type="text"
                                        value={newClientInfo.company_name}
                                        onChange={(e) => handleNewClientInputChange('company_name', e.target.value)}
                                        placeholder="Nom de la société"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Téléphone *</label>
                                    <input
                                        type="tel"
                                        value={newClientInfo.phone}
                                        onChange={(e) => handleNewClientInputChange('phone', e.target.value)}
                                        placeholder="Numéro de téléphone"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        value={newClientInfo.email}
                                        onChange={(e) => handleNewClientInputChange('email', e.target.value)}
                                        placeholder="Adresse email"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Matricule fiscal</label>
                                    <input
                                        type="text"
                                        value={newClientInfo.tax_registration_number}
                                        onChange={(e) => handleNewClientInputChange('tax_registration_number', e.target.value)}
                                        placeholder="Matricule fiscal"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Code douane</label>
                                    <input
                                        type="text"
                                        value={newClientInfo.customs_code}
                                        onChange={(e) => handleNewClientInputChange('customs_code', e.target.value)}
                                        placeholder="Code douane"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Catégorie</label>
                                    <select
                                        value={newClientInfo.categories}
                                        onChange={(e) => handleNewClientInputChange('categories', e.target.value)}
                                    >
                                        <option value="detaillant">Détaillant</option>
                                        <option value="grossiste">Grossiste</option>
                                        <option value="monoprix">Grande surface</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="cfp-form-section">
                            <h3>Informations Boutique *</h3>
                            <div className="cfp-form-grid">
                                <div className="cfp-form-group">
                                    <label>Nom de la boutique *</label>
                                    <input
                                        type="text"
                                        value={newShopInfo.name}
                                        onChange={(e) => handleNewShopInputChange('name', e.target.value)}
                                        placeholder="Nom du magasin"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Numéro</label>
                                    <input
                                        type="text"
                                        value={newShopInfo.address_number}
                                        onChange={(e) => handleNewShopInputChange('address_number', e.target.value)}
                                        placeholder="Numéro"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Rue</label>
                                    <input
                                        type="text"
                                        value={newShopInfo.street}
                                        onChange={(e) => handleNewShopInputChange('street', e.target.value)}
                                        placeholder="Rue"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Ville *</label>
                                    <input
                                        type="text"
                                        value={newShopInfo.ville}
                                        onChange={(e) => handleNewShopInputChange('ville', e.target.value)}
                                        placeholder="Ville"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Code postal</label>
                                    <input
                                        type="text"
                                        value={newShopInfo.postal_code}
                                        onChange={(e) => handleNewShopInputChange('postal_code', e.target.value)}
                                        placeholder="Code postal"
                                    />
                                </div>
                                <div className="cfp-form-group">
                                    <label>Pays *</label>
                                    <select
                                        value={newShopInfo.country}
                                        onChange={(e) => handleCountryChange(e.target.value)}
                                    >
                                        <option value="">Choisir un pays</option>
                                        {countries.map((country, idx) => (
                                            <option key={idx} value={country.code}>
                                                {country.flag} {country.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {newShopInfo.country === 'TUN' && (
                                    <div className="cfp-form-group">
                                        <label>Gouvernorat *</label>
                                        <select
                                            value={newShopInfo.region}
                                            onChange={(e) => handleNewShopInputChange('region', e.target.value)}
                                        >
                                            <option value="">Choisir un gouvernorat</option>
                                            {TunisiaCities?.map((city, idx) => (
                                                <option key={idx} value={city.name}>
                                                    {city.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Non-Tunisia Notice - shown when shop is selected and not Tunisia */}
                {((clientType === 'existing' && selectedShop && !isTunisia) || (clientType === 'new' && newShopInfo.country && newShopInfo.country !== 'TUN')) && (
                    <div className="cfp-non-tunisia-notice">
                        <icons.BsGlobe />
                        <span>Boutique hors Tunisie - TVA et timbre non applicables</span>
                    </div>
                )}

                {/* Products Section - Always visible */}
                <div className="cfp-layout">
                    {/* Products List */}
                    <div className="cfp-products">
                        <div className="cfp-products-header">
                            <h3>Produits disponibles</h3>
                            <div className="cfp-filters">
                                <input
                                    type="text"
                                    placeholder="Rechercher un produit..."
                                    value={searchProduct}
                                    onChange={(e) => setSearchProduct(e.target.value)}
                                />
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    <option value="">Toutes les catégories</option>
                                    {listCategoriesProducts?.map(cat => (
                                        <option key={cat._id} value={cat.designation}>
                                            {cat.designation}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {loading ? (
                            <LoaderComponent />
                        ) : (
                            <div className="cfp-products-grid">
                                {filteredProducts?.map((product) => (
                                    <div className="cfp-product-card" key={product._id}>
                                        <div className="cfp-product-image">
                                            <img src={product?.image?.secure_url} alt={product?.designation} />
                                        </div>
                                        <div className="cfp-product-info">
                                            <h4>{product?.designation}</h4>
                                            <span className="cfp-product-category">
                                                {getCategoryName(product)}
                                            </span>
                                            <span className="cfp-product-price">
                                                {product?.[typePrice(clientCategory)] || product?.Retail_Price} DT
                                            </span>
                                            {isTunisia && product?.Tva > 0 && (
                                                <span className="cfp-product-tva">TVA: {product?.Tva}%</span>
                                            )}
                                        </div>
                                        <button
                                            className="cfp-add-btn"
                                            onClick={() => handleAddToCart(product)}
                                        >
                                            <icons.BsPlus />
                                            Ajouter
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {filteredProducts?.length === 0 && !loading && (
                            <div className="cfp-empty">
                                <p>Aucun produit trouvé</p>
                            </div>
                        )}
                    </div>

                    {/* Cart Summary - Always visible */}
                    <div className="cfp-cart">
                        <h3 className="cfp-cart-title">Panier</h3>

                        {cart.length === 0 ? (
                            <div className="cfp-cart-empty">
                                <span>🛒</span>
                                <p>Aucun produit ajouté</p>
                            </div>
                        ) : (
                            <>
                                <div className="cfp-cart-items">
                                    {cart.map((item) => (
                                        <div className="cfp-cart-item" key={item.product._id}>
                                            <div className="cfp-cart-item-info">
                                                <span className="cfp-cart-item-name">{item.product?.designation}</span>
                                                <span className="cfp-cart-item-unit-price">
                                                    {item.product?.[typePrice(clientCategory)] || item.product?.Retail_Price} DT / unité
                                                </span>
                                            </div>
                                            <div className="cfp-cart-item-controls">
                                                <div className="cfp-qty-control">
                                                    <button onClick={() => handleQuantityChange(item.product._id, item.quantity - 1)}>-</button>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={item.quantity}
                                                        onChange={(e) => handleQuantityChange(item.product._id, e.target.value)}
                                                    />
                                                    <button onClick={() => handleQuantityChange(item.product._id, item.quantity + 1)}>+</button>
                                                </div>
                                                <div className="cfp-remise-control">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={item.remise}
                                                        onChange={(e) => handleRemiseChange(item.product._id, e.target.value)}
                                                        placeholder="Remise %"
                                                    />
                                                    <span>%</span>
                                                </div>
                                                <div className="cfp-cart-item-total">
                                                    <span>{handleCalculatePrice(item)} DT</span>
                                                </div>
                                                <button
                                                    className="cfp-remove-btn"
                                                    onClick={() => handleRemoveFromCart(item.product._id)}
                                                >
                                                    <icons.FaTrashAlt />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="cfp-cart-summary">
                                    <div className="cfp-summary-row">
                                        <span>Total articles</span>
                                        <strong>{cart.reduce((acc, item) => acc + item.quantity, 0)}</strong>
                                    </div>
                                    <div className="cfp-summary-row">
                                        <span>Total HT</span>
                                        <strong>{totalHT()} DT</strong>
                                    </div>
                                    <div className="cfp-summary-row total">
                                        <span>Total TTC</span>
                                        <strong className="cfp-total-price">{totalTCC()} DT</strong>
                                    </div>
                                    {isTunisia && (
                                        <div className="cfp-summary-row" style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                            <span>Timbre inclus</span>
                                            <span>1 DT</span>
                                        </div>
                                    )}
                                </div>

                                <button
                                    className="cfp-submit-btn"
                                    disabled={!isFormValidForSubmission() || isSubmitting}
                                    onClick={handleSubmitFacture}
                                >
                                    {isSubmitting ? (
                                        <span>Création en cours...</span>
                                    ) : (
                                        <>
                                            <icons.BsCheckCircle />
                                            Créer la facture proforma
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateFactureProforma;